import type { CSSProperties, ElementType, ReactNode } from 'react';

import './Text.css';

export type TextSize = 'md' | 'sm' | 'xs';
export type TextAlign = 'left' | 'center' | 'right';
export type TextAccent = 'text' | 'soft' | 'inactive' | 'strong';

export interface TextProps {
  /** The content of the text. */
  children: ReactNode;
  /** The size of the text. Defaults to `md`. */
  size?: TextSize;
  /** Text alignment. */
  align?: TextAlign;
  /** The color of the text. */
  accent?: TextAccent;
  /** If true, makes the text bold. */
  bold?: boolean;
  /** If true, truncates overflowing text with an ellipsis. */
  overflow?: boolean;
  /** Extra class name(s) applied to the element. */
  className?: string;
  /** Inline styles applied to the element. */
  style?: CSSProperties;
  /** The HTML element (or component) to render as. Defaults to `p`. */
  as?: ElementType;
}

/**
 * Styled with `--nj-semantic-color-*` tokens so it fits the `creative-ui`
 * design system instead of relying on Engie's global stylesheet.
 */
export function Text({
  children,
  size = 'md',
  align,
  accent,
  bold = false,
  overflow = false,
  className,
  style,
  as,
}: Readonly<TextProps>) {
  const Component = as ?? 'p';

  const classNames = [
    'c-text',
    `c-text--${size}`,
    bold ? 'c-text--bold' : '',
    overflow ? 'c-text--overflow' : '',
    align ? `c-text--${align}` : '',
    accent ? `c-text--${accent}` : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classNames} style={style}>
      {children}
    </Component>
  );
}

export default Text;
